import { css } from '@emotion/css';
import { Box, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NextIcon from 'assets/icons/next.svg';
import PreviousIcon from 'assets/icons/previous.svg';
import { func, number } from 'prop-types';
import React, { useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import { I18n } from 'utils/i18n';

const usePaginationStyles = () => {
  const theme = useTheme();
  return useMemo(() => {
    const disabled = css({ visibility: 'hidden' });
    const active = css({
      '& a': {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.selected,
        fontWeight: 'bold',
      },
    });
    const page = css({
      border: `1px solid ${theme.palette.divider}`,
      marginLeft: '-1px',
      display: 'flex',
      overflow: 'hidden',
      '& > a': {
        '& > div': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        marginLeft: '-1px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        height: 34,
        minWidth: 39,
        ...theme.typography.body1,
        outline: 'none',
        color: theme.palette.text.primary,
      },
      [`&.${disabled} + &`]: {
        borderRadius: '5px 0 0 5px',
      },
      [`&:nth-last-child(2).${active}`]: {
        borderRadius: '0 5px 5px 0',
      },
      [`&.${disabled} + &:nth-last-child(2).${active}`]: {
        borderRadius: '5px',
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    });
    const previous = css({ borderRadius: '5px 0 0 5px' });
    const next = css({ borderRadius: '0 5px 5px 0' });
    const arrow = css({
      width: 16,
      marginLeft: '10px',
      marginRight: '10px',
      color: theme.palette.primary.main,
    });
    const nextText = css({ marginLeft: '10px', marginRight: '20px' });
    const previousText = css({ marginRight: '10px', marginLeft: '20px' });

    return {
      wrapper: css({ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }),
      container: css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '5px',
        padding: 0,
        margin: 0,
      }),
      page,
      disabled,
      active,
      previous,
      next,
      arrow,
      nextText,
      previousText,
    };
  }, [theme]);
};

const Pagination = ({ total, perPage = 25, page, onChange }) => {
  const classes = usePaginationStyles();
  const pageCount = total > 0 && perPage > 0 ? Math.ceil(total / perPage) : 0;
  if (pageCount <= 1) {
    return <Box sx={{ height: 36 }} />;
  }
  return (
    <div className={classes.wrapper}>
      <ReactPaginate
        containerClassName={classes.container}
        pageClassName={classes.page}
        previousLabel={
          <div>
            <SvgIcon className={classes.arrow}>
              <PreviousIcon />
            </SvgIcon>
            <span className={classes.previousText}>{I18n.t('Pagination.Previous')}</span>
          </div>
        }
        nextLabel={
          <div>
            <span className={classes.nextText}>{I18n.t('Pagination.Next')}</span>
            <SvgIcon className={classes.arrow}>
              <NextIcon />
            </SvgIcon>
          </div>
        }
        previousClassName={`${classes.page} ${classes.previous}`}
        nextClassName={`${classes.page} ${classes.next}`}
        breakClassName={classes.page}
        pageCount={pageCount}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={onChange}
        activeClassName={classes.active}
        forcePage={page}
        disabledClassName={classes.disabled}
      />
    </div>
  );
};

Pagination.propTypes = {
  onChange: func.isRequired,
  page: number.isRequired,
  perPage: number,
  total: number.isRequired,
};

export default Pagination;
